import {
    ChevronDoubleLeftIcon,
    Cog6ToothIcon,
    InformationCircleIcon,
    QuestionMarkCircleIcon
} from '@heroicons/react/24/outline'
import useQueryParams from '../hooks/useQueryParams'
import { useEffect, useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import logo from '../assets/brand/arwen-logo-red-medium.png'
import logoMark from '../assets/brand/arwen-icon-red-small.png'
import ProfileButton from '../components/profile/ProfileButton'
import SidebarNav from '../components/sidebar/SidebarNav'
import { useAuth } from '../auth/AuthContext'
import { isUserAdminState, sidebarOpenState } from '../store/DomainStore'
import { classNames } from '../util/classNames'
import { stringToBoolean } from '../util/conversions'

// Rail widths. Collapsed is a 68px icon rail (not zero width) so the nav stays
// usable and the Arwen mark stays visible. The edge-handle offsets below are
// derived from these — keep them in step.
const RAIL_OPEN = 240 // w-60
const RAIL_COLLAPSED = 68 // w-[68px]
const HANDLE = 24 // edge-handle diameter (h-6/w-6)

// Foot links (Support, About, Admin) share one style: a plain ink link that fills
// on hover, and — when active — an ink-100 pill with the red tab drawn down its left
// edge. This is the static counterpart to the scrolling nav's sliding indicator; the
// foot is a separate container, so these draw their own active state.
type FootNavLinkProps = {
    href: string
    label: string
    icon: typeof Cog6ToothIcon
    active: boolean
    sidebarOpen: boolean
}

function FootNavLink({ href, label, icon: Icon, active, sidebarOpen }: FootNavLinkProps) {
    return (
        <a
            href={href}
            aria-current={active ? 'page' : undefined}
            title={!sidebarOpen ? label : undefined}
            className={classNames(
                active ? 'bg-ink-100 text-ink-900' : 'text-ink-700 hover:bg-ink-50 hover:text-ink-900',
                sidebarOpen ? 'gap-3 px-3' : 'justify-center px-0',
                'group relative mb-2 flex w-full items-center overflow-hidden rounded-md py-2 text-sm font-medium'
            )}
        >
            {active && (
                <span className="absolute bottom-2 left-0 top-2 w-0.5 rounded-full bg-arwen-600" />
            )}
            <Icon
                className={classNames(
                    active ? 'text-ink-700' : 'text-ink-400 group-hover:text-ink-600',
                    'h-5 w-5 flex-none'
                )}
                aria-hidden="true"
            />
            {sidebarOpen && <span className="truncate">{label}</span>}
        </a>
    )
}

function Sidebar({ children }: any) {
    const { signOut } = useAuth()
    // Shared atom (persisted to localStorage) so the admin secondary rail
    // collapses in lockstep with this one.
    const [sidebarOpen, setSidebarOpen] = useRecoilState(sidebarOpenState)
    const toggleSidebar = () => setSidebarOpen(!sidebarOpen)

    const [admin] = useRecoilState(isUserAdminState)
    // Highlight each foot link across its area. Admin spans the whole /admin tree;
    // Support and About are single pages.
    const { pathname } = useLocation()
    const onAdminArea = pathname.startsWith('/admin')
    const onSupport = pathname.startsWith('/support')
    const onAbout = pathname.startsWith('/about')

    // When we're in the onboarding flow we hide the collapse controls.
    // The onboarding state can be reset by appending `&onboarding=false` to the URL.
    // Going to the homepage is the no-more-onboarding-please escape hatch (AccountsPage).
    const [onboarding, setOnboarding] = useState(() => {
        const saved = localStorage.getItem('onboarding')
        return saved === 'true'
    })
    useEffect(() => {
        localStorage.setItem('onboarding', onboarding.toString())
    }, [onboarding])
    const onboardingParam = stringToBoolean(useQueryParams().get('onboarding'))
    useEffect(() => {
        if (onboardingParam !== null) {
            setOnboarding(onboardingParam)
        }
    }, [onboarding, onboardingParam])

    // The collapse control is the edge handle that straddles the rail's right
    // border. Hidden during onboarding.
    const showToggles = !onboarding
    const label = sidebarOpen ? 'Collapse navigation' : 'Expand navigation'
    // Centre the edge handle on the rail's right border so it straddles the edge.
    const handleLeft = (sidebarOpen ? RAIL_OPEN : RAIL_COLLAPSED) - HANDLE / 2

    return (
        <div className="full-screen">
            {/* Fixed nav rail: 240px open, 68px icon rail when collapsed. overflow-visible
                is explicit to override the legacy `.full-screen > *` rule in index.css,
                which forces overflow:auto on every direct child of .full-screen — with
                that, the account menu was clipped to the 68px rail when collapsed, and
                during expand a scrollbar flashed because the open-state content (wordmark,
                nav labels, profile email) renders at full width while the rail is still
                animating up from 68px. With the rail visible the menu escapes freely, and
                the header + foot controls clip their own horizontal overflow
                (overflow-hidden) so the transient wide content reveals cleanly instead of
                spilling. The nav list clips itself below. */}
            <div
                className={`transition-[width] fixed inset-y-0 z-20 flex flex-col overflow-visible ${
                    sidebarOpen ? 'w-60' : 'w-[68px]'
                }`}
            >
                <div className="flex h-full flex-col border-r border-ink-200 bg-white">
                    {/* Rail header — wordmark (open) collapses to the icon mark; both link home */}
                    <div
                        className={`flex h-16 flex-none items-center overflow-hidden border-b border-ink-100 ${
                            sidebarOpen ? 'px-4' : 'justify-center px-0'
                        }`}
                    >
                        <Link to="/" aria-label="Arwen home" className="flex items-center">
                            {sidebarOpen ? (
                                <img src={logo} alt="Arwen" className="w-[132px]" />
                            ) : (
                                <img src={logoMark} alt="Arwen" className="h-8 w-8" />
                            )}
                        </Link>
                    </div>

                    {/* Nav list owns the vertical scroll and horizontal clip */}
                    <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-2">
                        <SidebarNav sidebarOpen={sidebarOpen} />
                    </div>

                    {/* Foot: the utility cluster (Support, About, then Admin for admins)
                        above the account menu. These sit here — anchored below the
                        scrolling product nav — because they're support/mode-switch
                        surfaces, not core workspace pages. */}
                    <div className="flex-none border-t border-ink-100 p-3">
                        <FootNavLink
                            href="#/support"
                            label="Support"
                            icon={QuestionMarkCircleIcon}
                            active={onSupport}
                            sidebarOpen={sidebarOpen}
                        />
                        <FootNavLink
                            href="#/about"
                            label="About"
                            icon={InformationCircleIcon}
                            active={onAbout}
                            sidebarOpen={sidebarOpen}
                        />
                        {admin && (
                            <FootNavLink
                                href="#/admin"
                                label="Admin"
                                icon={Cog6ToothIcon}
                                active={onAdminArea}
                                sidebarOpen={sidebarOpen}
                            />
                        )}
                        <ProfileButton signOut={signOut} sidebarOpen={sidebarOpen} />
                    </div>
                </div>
            </div>

            {/* Edge handle: circular chevron straddling the rail's right border.
                Fixed so it tracks the fixed rail and is never clipped. */}
            {showToggles && (
                <button
                    type="button"
                    onClick={toggleSidebar}
                    aria-label={label}
                    style={{ left: `${handleLeft}px` }}
                    className="transition-[left] fixed top-[76px] z-30 flex h-6 w-6 items-center justify-center rounded-full border border-ink-200 bg-white text-ink-500 shadow-md hover:border-ink-300 hover:text-arwen-600 focus:outline-none focus:ring-2 focus:ring-arwen-500"
                >
                    <ChevronDoubleLeftIcon
                        className={`h-4 w-4 transition-transform ${sidebarOpen ? '' : 'rotate-180'}`}
                        aria-hidden="true"
                    />
                </button>
            )}

            {/* Content column — padded to clear the fixed rail */}
            <div
                className={`transition-[padding] flex flex-1 flex-col full-screen ${
                    sidebarOpen ? 'pl-60' : 'pl-[68px]'
                }`}
            >
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    {/* Grey lives on the scroll container so it always spans the full
                        scroll height (page roots are flex-shrunk to the viewport) */}
                    <main className="relative flex min-h-0 flex-1 flex-col overflow-y-auto bg-ink-50">
                        {!!children ? children : <Outlet />}
                    </main>
                </div>
            </div>
        </div>
    )
}

export default Sidebar
